import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { actionCreator, stateSelectors } from './redux';
import config from '../../../config';
import { Page } from "../page/Page";
import ContactInfo from "../../components/contact-info/ContactInfo";
import button from '../../components/button';
import alert from '../../components/alert';
import { Icon, Modal, Type, Stack, Box, Table } from "react-spark";

const Button = button.baseButton;
const Alert = alert.baseAlert;
const {
    creditNoHitFreeze, creditNoHitFreezeTu, creditNoHitFreezeEqTu,
} = config.global.api.responseCodes.errorCodes;

const CREDIT_FREEZE_AGENCY = Object.freeze({
    EQUIFAX: 0,
    TRANSUNION: 1,
    BOTH: 2
});

export class CreditFreeze extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showModal: false,
            modalUrl: '',
            modalTitle: '',
            surpassedCreditAttempts: false,
            reportingAgencyModal: '',
            creditFreezeAgency: ''
        };
    }

    componentDidMount() {
        const pathname = this.props.location && this.props.location.pathname;
        const url = this.props.url;

        const equifaxCreditFreeze = pathname === '/credit-freeze-eq' || url === '/credit-freeze-eq';
        const transUnionCreditFreeze = pathname === '/credit-freeze-tu' || url === '/credit-freeze-tu';
        const equifaxAndTransUnionCreditFreeze = pathname === '/credit-freeze' || url === '/credit-freeze';

        this.setState({
            creditFreezeAgency: equifaxAndTransUnionCreditFreeze
                ? CREDIT_FREEZE_AGENCY.BOTH
                : equifaxCreditFreeze
                    ? CREDIT_FREEZE_AGENCY.EQUIFAX
                    : transUnionCreditFreeze
                        ? CREDIT_FREEZE_AGENCY.TRANSUNION
                        : CREDIT_FREEZE_AGENCY.BOTH
        });
    }

    componentWillUnmount() {
        this.props.reset();
    }

    handleTransUnionClick = () => {
        this.setState({
            showModal: true,
            modalUrl: 'https://www.transunion.com/credit-freeze',
            modalTitle: 'The TransUnion website will open in another tab.',
            reportingAgencyModal: 'TransUnion'
        });
    };

    handleEquifaxClick = () => {
        this.setState({
            showModal: true,
            modalUrl: 'https://www.equifax.com/personal/credit-report-services/credit-freeze/',
            modalTitle: 'The Equifax website will open in another tab.',
            reportingAgencyModal: 'Equifax'
        });
    };

    handleCloseModal = () => {
        this.setState({ showModal: false });
    };

    handlePullCreditUnfreeze = () => {
        const failureData = JSON.parse(localStorage.getItem('creditFailureData')) || { count: 0, timestamp: Date.now() };
        const currentTime = Date.now();
        const oneHour = 60 * 60 * 1000;

        if (failureData.count >= 3 && currentTime - failureData.timestamp <= oneHour) {
            this.setState({ surpassedCreditAttempts: true });
        } else {
            this.setState({ surpassedCreditAttempts: false });
            this.props.pullCreditUnfreeze();
        }
    };

    render() {
        const { pullingCredit, code, reset } = this.props;
        const { showModal, modalUrl, modalTitle, surpassedCreditAttempts, reportingAgencyModal, creditFreezeAgency } = this.state;
        const creditAgencyName = {
            [CREDIT_FREEZE_AGENCY.EQUIFAX]: 'Equifax',
            [CREDIT_FREEZE_AGENCY.TRANSUNION]: 'TransUnion',
            [CREDIT_FREEZE_AGENCY.BOTH]: 'Equifax and TransUnion'
        }[creditFreezeAgency];

        return (
            <Page id="creditFreezeNotice" heading="Credit Freeze Notice">
                <Stack itemSpacing="large">
                    <Stack.Item>
                        <Type.BodyTwo className='sprk-u-mbn'>
                            It looks like you have a credit freeze on your credit report and unfortunately we are unable to continue until you contact {creditAgencyName} to remove the freeze. To lift or remove the credit freeze, please contact {creditAgencyName} through one of the following below. Please note, it may take up to 24 hours to remove the freeze.
                        </Type.BodyTwo>
                    </Stack.Item>

                    {creditFreezeAgency === CREDIT_FREEZE_AGENCY.BOTH || creditFreezeAgency === CREDIT_FREEZE_AGENCY.TRANSUNION ? (
                        <Stack.Item>
                            <Type.DisplayFour>TransUnion</Type.DisplayFour>
                            <Table
                                variant="secondary"
                                striped
                                className="sprk-b-Table--spacing-medium"
                            >
                                <tbody className="credit-freeze-table">
                                    <tr>
                                        <td data-testid="transunion-unfreeze-online">Unfreeze Online with TransUnion</td>
                                        <td data-testid="transunion-unfreeze-online-value">
                                            <button
                                                onClick={this.handleTransUnionClick}
                                                className="sprk-c-Link credit-freeze-stylized-link"
                                            >
                                                https://www.transunion.com/credit-freeze
                                            </button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-testid="transunion-automated-service-line">
                                            TransUnion Service Center
                                            <br />
                                            Monday - Friday 9:00 a.m. - 11:00 p.m. ET
                                            <br />
                                            Saturday and Sunday 8:00 a.m. - 5:00 p.m. ET
                                        </td>
                                        <td data-testid="transunion-automated-service-line-value">
                                            800-916-8800
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Stack.Item>
                    ) : null}

                    {this.state.creditFreezeAgency === CREDIT_FREEZE_AGENCY.BOTH || this.state.creditFreezeAgency === CREDIT_FREEZE_AGENCY.EQUIFAX ? (
                        <Stack.Item>
                            <Type.DisplayFour>Equifax</Type.DisplayFour>
                            <Table
                                variant="secondary"
                                striped
                                className="sprk-b-Table--spacing-medium"
                            >
                                <tbody className="credit-freeze-table">
                                    <tr>
                                        <td data-testid="equifax-unfreeze-online">Unfreeze Online with Equifax</td>
                                        <td data-testid="equifax-unfreeze-online-value">
                                            <button
                                                onClick={this.handleEquifaxClick}
                                                className="sprk-c-Link credit-freeze-stylized-link"
                                            >
                                                https://www.equifax.com/personal/credit-report-services/credit-freeze/
                                            </button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-testid="equifax-automated-service-line">
                                            Automated Service Line
                                            <br />
                                            7:30 a.m. - 1:30 a.m. (ET) 7 days a week
                                        </td>
                                        <td data-testid="equifax-automated-service-line-value">
                                            800-685-1111
                                            <br />
                                            800-349-9960 (NY Residents)
                                        </td>
                                    </tr>
                                    <tr>
                                        <td data-testid="equifax-customer-care-line">
                                            Customer Care Line
                                            <br />
                                            8:00 a.m. - 8:00 p.m. (ET) Monday - Friday
                                        </td>
                                        <td data-testid="equifax-customer-care-line-value">800-298-0045</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Stack.Item>
                    ) : null}



                    <Stack splitAt="small" itemSpacing="medium" className="sprk-u-pbm">
                        <Stack.Item width="three-fourths">
                            <Box padding="flush">
                                <Alert type="info" isDisplayed>
                                    <Icon name="exclamation" style={{ stroke: 'black', fill: 'black' }} />
                                    If you have already contacted {creditAgencyName} click continue to proceed:
                                </Alert>
                            </Box>
                        </Stack.Item>
                        <Stack.Item
                            width="fourth"
                            className="sprk-o-Stack__item--center-column"
                        >
                            <Box padding="flush">
                                <Button
                                    id="continueCreditFreeze"
                                    name="continueCreditFreeze"
                                    disabled={pullingCredit}
                                    loading={pullingCredit}
                                    onClick={this.handlePullCreditUnfreeze}
                                >
                                    Continue
                                </Button>
                            </Box>
                        </Stack.Item>
                    </Stack>

                    {surpassedCreditAttempts &&
                        <div className="sprk-u-pbm" style={{ maxWidth: '390px' }}>
                            <Type.BodyFour style={{ color: '#D47500' }}>
                                Too many soft credit check attempts. For your security, we've paused your application. Please try again in an hour.
                            </Type.BodyFour>
                        </div>
                    }

                    <Stack.Item className="sprk-u-pth sprk-u-pbl">
                        <ContactInfo config={config.global.company} />
                    </Stack.Item>
                </Stack>

                <Modal
                    id="creditAgencyNameModal"
                    type="info"
                    show={showModal}
                    onClose={this.handleCloseModal}
                    title={modalTitle}
                >
                    <Stack itemSpacing="large">
                        <Stack.Item>
                            <Type.BodyTwo>
                                Follow the instructions there to unfreeze your account, but make sure to leave this tab open. Closing or refreshing this page will clear your responses and take you to the start of the application.
                            </Type.BodyTwo>
                        </Stack.Item>
                        <Stack.Item className="sprk-u-TextAlign--right">
                            <a href={modalUrl} className="sprk-c-Button" target="_blank" rel="noopener noreferrer">Go to {reportingAgencyModal}</a>
                        </Stack.Item>
                    </Stack>
                </Modal>

                <Modal
                    id="creditFreezeModal"
                    type="info"
                    show={code === creditNoHitFreeze.code || code === creditNoHitFreezeTu.code || code === creditNoHitFreezeEqTu.code}
                    onClose={() => reset()}
                    title="There is still a credit freeze on your account."
                >
                    <Stack itemSpacing="large">
                        <Stack.Item>
                            <Type.BodyTwo>
                                Please contact {creditAgencyName} to lift or remove your credit freeze. If you've already contacted them, you may need to wait up to 24 hours for your credit to unfreeze.
                            </Type.BodyTwo>
                        </Stack.Item>
                        <Stack.Item className="sprk-u-TextAlign--right">
                            <Button onClick={() => reset()}>
                                Close
                            </Button>
                        </Stack.Item>
                    </Stack>
                </Modal>

                <Modal
                    id="creditPullErrorModal"
                    type="info"
                    show={code === 'CREDIT_PULL_ERROR'}
                    onClose={() => reset()}
                    title="There was a problem"
                >
                    <Type.BodyTwo>
                        <Icon name="exclamation" />
                        There was a problem with your request. Please call us at{' '}
                        {config.global.company.phone.dash}.
                    </Type.BodyTwo>
                </Modal>
            </Page>
        );
    }
}
CreditFreeze.defaultProps = {
    reset: () => { },
    pullingCredit: false,
    code: '',
};
CreditFreeze.propTypes = {
    pullingCredit: PropTypes.bool,
    reset: PropTypes.func,
    pullCreditUnfreeze: PropTypes.func.isRequired,
    code: PropTypes.string,
    url: PropTypes.string
};

const mapStateToProps = (state) => ({
    pullingCredit: stateSelectors.getPullingCredit(state),
    code: stateSelectors.getCode(state),
    error: stateSelectors.getReportError(state)
});

const mapDispatchToProps = (dispatch) => ({
    pullCreditUnfreeze: () => dispatch((_, getState) => {
        const state = getState();
        if (state && state.loan && state.loan.loanJson && state.loan.loanJson.loanId) {
            dispatch(actionCreator.pullCredit());
        } else {
            dispatch(actionCreator.pullCreditUnfreeze());
        }
    }),
    reset: () => dispatch(actionCreator.reset()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CreditFreeze);
